const TEMPORARY_API_STATUSES = new Set([502, 503, 504]);

const initialState = {
    status: "healthy",
    reason: null,
    lastHealthyAt: null,
    lastFailureAt: null,
    lastCheckedAt: null,
    consecutiveHealthCheckFailures: 0,
    detail: null,
};

let currentState = { ...initialState };
const listeners = new Set();
const buildTimeApiBaseUrl = process.env.VITE_API_URL || process.env.VITE_NODE_API_URL || "http://localhost:3000";

function emit(nextState) {
    currentState = {
        ...currentState,
        ...nextState,
    };
    listeners.forEach((listener) => listener(currentState));
}

export function getApiAvailabilityState() {
    return currentState;
}

export function resetApiAvailabilityState() {
    currentState = { ...initialState };
    listeners.clear();
}

export function subscribeApiAvailability(listener) {
    listeners.add(listener);
    listener(currentState);
    return () => {
        listeners.delete(listener);
    };
}

export function notifyApiHealthy(detail = {}) {
    emit({
        status: "healthy",
        reason: null,
        lastHealthyAt: new Date().toISOString(),
        lastCheckedAt: new Date().toISOString(),
        consecutiveHealthCheckFailures: 0,
        detail,
    });
}

export function notifyApiDegraded(reason = "temporary_unavailable", detail = {}) {
    const isHealthCheckFailure = detail.source === "health_check";
    const consecutiveHealthCheckFailures = isHealthCheckFailure
        ? currentState.consecutiveHealthCheckFailures + 1
        : currentState.consecutiveHealthCheckFailures;

    emit({
        status: "degraded",
        reason,
        lastFailureAt: new Date().toISOString(),
        lastCheckedAt: new Date().toISOString(),
        consecutiveHealthCheckFailures,
        detail: isHealthCheckFailure
            ? { ...detail, consecutiveHealthCheckFailures }
            : detail,
    });
}

export function getApiBaseUrl(env) {
    const raw = env
        ? env.VITE_API_URL || env.VITE_NODE_API_URL || "http://localhost:3000"
        : buildTimeApiBaseUrl;
    return raw.replace(/\/+$/, "");
}

export function isTemporaryApiFailure(error) {
    if (!error || error.__CANCEL__ || error.code === "ERR_CANCELED") {
        return false;
    }

    if (!error.response) {
        return true;
    }

    const status = error.response.status;
    if (status === 503 && error.response.data?.status === "MAINTENANCE") {
        return false;
    }

    return TEMPORARY_API_STATUSES.has(status);
}

export async function checkApiAvailability({
    apiBaseUrl = getApiBaseUrl(),
    fetchImpl = globalThis.fetch,
    timeoutMs = 8000,
} = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(`${apiBaseUrl}/health/ready?source=frontend-availability&t=${Date.now()}`, {
            cache: "no-store",
            headers: {
                "Cache-Control": "no-store",
                Pragma: "no-cache",
            },
            signal: controller.signal,
        });

        const body = await response.json().catch(() => ({}));
        if (response.ok && body.status === "ok") {
            notifyApiHealthy({
                source: "health_check",
                release: body.release || null,
                uptime: body.uptime || null,
            });
            return { ok: true, body };
        }

        notifyApiDegraded("health_check_failed", {
            source: "health_check",
            httpStatus: response.status,
            status: body.status || null,
        });
        return { ok: false, body, httpStatus: response.status };
    } catch (error) {
        notifyApiDegraded(error?.name === "AbortError" ? "health_check_timeout" : "health_check_error", {
            source: "health_check",
            message: error?.message || null,
        });
        return { ok: false, error };
    } finally {
        clearTimeout(timeout);
    }
}
